import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { LineReveal, Reveal } from "@/lib/motion";
import { toast } from "sonner";

const LOCATIONS = ["Washington DC", "New York", "Miami", "Other"];
const GUESTS = ["Under 50", "50–100", "100–200", "200–350", "350+"];
const BUDGETS = ["Under $5,000", "$5,000–$10,000", "$10,000–$25,000", "$25,000+", "Prefer to discuss"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[0.65rem] uppercase tracking-[0.22em] text-aria-muted">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full border-0 border-b border-aria-travertine bg-transparent py-3 font-body text-lg text-aria-ink placeholder:text-aria-muted/50 focus:border-aria-brass focus:outline-none focus:ring-0 transition-colors";

export default function Book() {
  const [eventTypes, setEventTypes] = useState([]);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_date: "", venue: "", location: "",
    guest_count: "", event_type: "", planner: "", budget: "", notes: "",
  });

  useEffect(() => {
    document.title = "Book ARIA — The Inquiry";
    endpoints.eventTypes().then(setEventTypes).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email so we may respond.");
      return;
    }
    setSubmitting(true);
    try {
      await endpoints.createInquiry(form);
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email events@ariadrinks.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="aria-container flex min-h-[80svh] flex-col justify-center py-40" data-testid="book-success">
        <div className="eyebrow mb-8">Received</div>
        <h1 className="max-w-4xl font-display text-5xl md:text-7xl leading-[0.98] text-aria-ink">
          <LineReveal lines={["Thank you.", "We will be in touch."]} />
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-10 max-w-xl font-light text-lg leading-relaxed text-aria-cocoa/80">
            Your enquiry has reached our team. We respond personally to every request and will write to you shortly to begin composing your experience.
          </p>
        </Reveal>
      </div>
    );
  }

  return (
    <div data-testid="book-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        {/* Left editorial column */}
        <div className="bg-aria-ink text-aria-ivory md:col-span-5">
          <div className="flex h-full flex-col justify-between px-6 py-40 md:px-16 md:py-52">
            <div>
              <div className="eyebrow mb-8">Book ARIA</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-aria-ivory">
                <LineReveal lines={["Begin", "the", "conversation."]} />
              </h1>
              <p className="mt-10 max-w-sm font-light text-lg leading-relaxed text-aria-ivory/60">
                Share a few details about your occasion. We accept a limited number of engagements each season and reply to every enquiry personally.
              </p>
            </div>
            <div className="mt-16 flex gap-8 text-[0.7rem] uppercase tracking-[0.2em] text-aria-brass">
              <span>Washington DC</span>
              <span>New York</span>
              <span>Miami</span>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="md:col-span-7">
          <form onSubmit={submit} className="px-6 py-24 md:px-16 md:py-52" data-testid="book-form">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2">
              <Field label="Full Name">
                <input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
              </Field>
              <Field label="Email">
                <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </Field>
              <Field label="Phone">
                <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Optional" />
              </Field>
              <Field label="Event Date">
                <input data-testid="input-date" type="date" className={inputCls} value={form.event_date} onChange={set("event_date")} />
              </Field>
              <Field label="Event Type">
                <select data-testid="select-event-type" className={inputCls} value={form.event_type} onChange={set("event_type")}>
                  <option value="">Select</option>
                  {eventTypes.map((et) => (
                    <option key={et.id} value={et.name}>{et.name}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </Field>
              <Field label="Location">
                <select data-testid="select-location" className={inputCls} value={form.location} onChange={set("location")}>
                  <option value="">Select</option>
                  {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Venue">
                <input data-testid="input-venue" className={inputCls} value={form.venue} onChange={set("venue")} placeholder="If known" />
              </Field>
              <Field label="Guest Count">
                <select data-testid="select-guests" className={inputCls} value={form.guest_count} onChange={set("guest_count")}>
                  <option value="">Select</option>
                  {GUESTS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Planner / Company">
                <input data-testid="input-planner" className={inputCls} value={form.planner} onChange={set("planner")} placeholder="Optional" />
              </Field>
              <Field label="Estimated Budget">
                <select data-testid="select-budget" className={inputCls} value={form.budget} onChange={set("budget")}>
                  <option value="">Select</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Field>
            </div>

            <div className="mt-10">
              <Field label="Tell us about your occasion">
                <textarea data-testid="input-notes" rows={4} className={`${inputCls} resize-none`} value={form.notes} onChange={set("notes")} placeholder="A few words on your vision, the setting, the guests…" />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="book-submit"
              disabled={submitting}
              className="mt-14 inline-flex items-center gap-4 bg-aria-ink px-10 py-5 text-[0.72rem] uppercase tracking-[0.24em] text-aria-ivory transition-colors hover:bg-aria-cocoa disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Submit Enquiry"}
              <span aria-hidden>→</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
